/**
 * WHERE A LEAD CAME FROM, AS A PLACE, NOT AS A PIPE.
 *
 * Anthony, 2026-10-20: "track what campaign it came from instead of things
 * like callrail, ghl etc" -- "like GMB Hemet", "Supercanic Form Fill", "in
 * small ways basically", "like Sup-FF", "Supshop-GMB".
 *
 * `enquiries.source` says how a lead reached us: `callrail` for every call
 * on every line, the bare domain for a form. That column stays exactly as
 * it is, because lead-sources.ts fetches on it and a renamed value is a
 * lead the opener silently never sees. This is a LABEL computed on read,
 * from facts already on file: the line a call rang (comm_events
 * .tracking_number) and the site a form was filled on (the source domain).
 * Nothing is migrated, so every old lead gets its tag too.
 *
 * Tags are short so they fit the tile foot: PLACE-KIND. GMB is a Google
 * Business Profile line, FF a form fill, Web a number shown on a site,
 * TXT a text to that line, Call a line that is neither.
 */

export type Campaign = { tag: string; name: string };

type Line = { place: string; kind: "GMB" | "Web" | "Call"; name: string };

/**
 * CallRail lines, ten digits. Names are the CallRail tracker names as of
 * the 2026-09-20 pull (_ops/callrail-summary-2026-09-20.json), matched to
 * numbers by call count, and the profiles in memory location-numbers-rule.
 */
export const LINES: Record<string, Line> = {
  "9519042477": { place: "Supshop", kind: "GMB", name: "Supercanic Shop GMB, San Jacinto (CallRail: Supercanic Garage)" },
  "9514741277": { place: "Hem", kind: "GMB", name: "Hemet GMB, Hemet Mobile Mechanic (CallRail: Google My Business Hemet)" },
  "9517202842": { place: "Riv", kind: "GMB", name: "Riverside GMB, 3959 Market St (CallRail: Mobile Mechanic Riverside)" },
  "3103406775": { place: "Haw", kind: "GMB", name: "Hawthorne GMB (CallRail: Supercanic Hawthorne)" },
  "9285045772": { place: "Hav", kind: "GMB", name: "Lake Havasu GMB (CallRail: Havasu)" },
  "9715252111": { place: "Port", kind: "GMB", name: "Portland GMB" },
  "9514740744": { place: "MMSJ", kind: "GMB", name: "Mobile Mechanic San Jacinto GMB, autorepairsj.com via 633-8580" },
  "9517203622": { place: "Riv", kind: "Web", name: "Riverside site number (CallRail: Supercanic Riverside)" },
  "9516441599": { place: "Sup", kind: "Web", name: "Supercanic.com phone number (CallRail: Supercanic Website)" },
  "9516442767": { place: "SupHem", kind: "Web", name: "Supercanic Hemet page number (CallRail: Supercanic Hemet)" },
  "9516181982": { place: "Tem", kind: "Web", name: "Temecula Mobile Mechanics number" },
  "9512678500": { place: "PPI", kind: "Web", name: "PrePurchaseAuto.com number" },
  "8669481670": { place: "MR", kind: "Call", name: "MechanicRank 866 line (AUTOFIX)" },
  "5022891947": { place: "LouLock", kind: "Call", name: "Louisville locksmith line" },
};

/** Sites whose forms post leads, by bare domain. Others fall back below. */
export const SITES: Record<string, { place: string; name: string }> = {
  "supercanic.com": { place: "Sup", name: "Supercanic.com" },
  "hemetmechanic.com": { place: "Hem", name: "Hemetmechanic.com" },
  "prepurchaseauto.com": { place: "PPI", name: "PrePurchaseAuto.com" },
  "mechanicrank.com": { place: "MR", name: "MechanicRank.com" },
  "mechanictemecula.com": { place: "Tem", name: "Mechanictemecula.com" },
  "mechanicmilwaukee.com": { place: "Mil", name: "Mechanicmilwaukee.com" },
  "vipcanic.com": { place: "VIP", name: "Vipcanic.com" },
  "autorepairsj.com": { place: "MMSJ", name: "Autorepairsj.com" },
  "riversidemechanics.com": { place: "Riv", name: "Riversidemechanics.com" },
};

/** Sources that are not a line or a site. Pipes never show as the tag. */
const OTHER: Record<string, Campaign> = {
  "ppi-desk": { tag: "PPI-Desk", name: "PPI request, mirrored from the PPI desk" },
  "counter": { tag: "Walk-in", name: "Added at the counter" },
  "desk": { tag: "Desk", name: "Added on the desk" },
  "csv": { tag: "Import", name: "Imported list" },
  "website": { tag: "Site-FF", name: "Website form fill" },
  "web chat": { tag: "Chat", name: "Website chat" },
  "email": { tag: "Email", name: "Email" },
  "fleet_portal": { tag: "Fleet", name: "Fleet portal" },
};

const ten = (s: string | null | undefined): string =>
  String(s ?? "").replace(/\D/g, "").slice(-10);

const DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/**
 * The tag for one lead. `line` is the number the call or text came in on,
 * when there is one on file. Never returns a pipe name: an unknown line
 * shows its last four digits, an unknown site its own name.
 */
export function campaignOf(source: string | null | undefined, line?: string | null): Campaign | null {
  const src = String(source ?? "").trim().toLowerCase();
  if (!src) return null;

  const isCall = src === "callrail" || src === "callrail-market" || src === "ghl-call";
  const isText = src === "callrail text";
  if (isCall || isText) {
    const n = ten(line);
    const l = LINES[n];
    const how = isText ? "text" : "call";
    if (l) {
      return { tag: `${l.place}-${isText ? "TXT" : l.kind}`, name: `${l.name}, ${how}` };
    }
    if (n.length === 10) {
      const shown = `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
      return { tag: `${isText ? "Text" : "Call"}-${n.slice(-4)}`, name: `${how} to ${shown}` };
    }
    return { tag: isText ? "Text" : "Call", name: `Phone ${how}, line not on file` };
  }

  const bare = src.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "");
  if (DOMAIN.test(bare)) {
    const s = SITES[bare];
    if (s) return { tag: `${s.place}-FF`, name: `${s.name} form fill` };
    const first = bare.split(".")[0]!;
    const place = first.charAt(0).toUpperCase() + first.slice(1, 6);
    return { tag: `${place}-FF`, name: `${bare} form fill` };
  }

  return OTHER[src] ?? { tag: String(source).trim(), name: String(source).trim() };
}

/**
 * The comm_events row that holds a call lead's line. CallRail call leads
 * keep the call id in external_ref and the event is `crcall:<id>`. Text
 * leads have no single event id, so their line is read off the person's
 * texts instead (src/data/leads.ts). Anything else has no line to read.
 */
export function lineEventId(source: string | null | undefined, ref: string | null | undefined): string | null {
  const src = String(source ?? "").toLowerCase();
  const r = String(ref ?? "").trim();
  if ((src === "callrail" || src === "callrail-market") && r.startsWith("CAL")) return `crcall:${r}`;
  return null;
}
